import { normalizeVersion } from '@/components/tiptap-editor/tiptap-extension/zyversion-extension/zyversion-extension';
import type { JSONContent } from '@tiptap/core';
import { v4 as uuidv4 } from 'uuid';

type ProsemirrorTextMark = { type: string; attrs?: Record<string, unknown> };

export type ZybookTestQuestionNodeType =
    | 'question'
    | 'instructions'
    | 'paragraph'
    | 'line'
    | 'codeBlock'
    | 'choices'
    | 'choice'
    | 'table'
    | 'row'
    | 'header'
    | 'cell'
    | 'image'
    | 'text';

export type ZybookTestQuestionTextNodeMark = 'ilatex' | 'blatex' | 'code' | 'italic' | 'bold';

export type ZybookTestQuestionNodeKind = 'block' | 'text';

export type ZybookTestQuestionNode = {
    kind: ZybookTestQuestionNodeKind;
    type?: ZybookTestQuestionNodeType;
    text?: string;
    marks?: { type?: ZybookTestQuestionTextNodeMark }[];
    nodes?: ZybookTestQuestionNode[] | null;
    data?: Record<string, unknown>;
    isVoid?: boolean;
};

export type ZybookTestQuestion = {
    test_question_id: number;
    payload: {
        kind: string;
        type: string;
        nodes: ZybookTestQuestionNode[];
    };
    guid?: string;
    gdoc_id?: string | null;
    dev_book?: string | null;
    gdoc_parent_folder?: string | null;
    canonical_section_id?: number;
    number?: number;
    release_id?: number;
    show_in_eval?: boolean;
    subject_id?: number;
    version?: string | null;
};

export type ConvertZybookTestQuestionsForImportOptions = ConvertZybookTestQuestionsOptions & {
    sameContentSourceSubjectCount: number;
};

export type ConvertZybookTestQuestionsOptions = {
    baseImageUrl?: string | null;
};

function getTestQuestionGuidKey(question: ZybookTestQuestion): string {
    return question.guid ?? `test_question_id:${question.test_question_id}`;
}

export function countUniqueSubjectsForGuid(questions: ZybookTestQuestion[], guid: string): number {
    const subjectIds = new Set<number>();
    for (const question of questions) {
        if (getTestQuestionGuidKey(question) === guid && question.subject_id != null) {
            subjectIds.add(question.subject_id);
        }
    }
    return subjectIds.size;
}

export function isUniversalTestQuestionGuid(questions: ZybookTestQuestion[], guid: string, subjectCount: number): boolean {
    return countUniqueSubjectsForGuid(questions, guid) === subjectCount;
}

export function pickRepresentativeTestQuestion(variants: ZybookTestQuestion[]): ZybookTestQuestion {
    return [...variants].sort((a, b) => {
        const releaseDiff = (b.release_id ?? 0) - (a.release_id ?? 0);
        if (releaseDiff !== 0) {
            return releaseDiff;
        }
        return b.test_question_id - a.test_question_id;
    })[0]!;
}

export function dedupeTestQuestionVariantsBySubject(variants: ZybookTestQuestion[]): ZybookTestQuestion[] {
    const bestBySubject = new Map<number, ZybookTestQuestion>();
    for (const question of variants) {
        if (question.subject_id == null) {
            continue;
        }
        const existing = bestBySubject.get(question.subject_id);
        if (
            !existing ||
            (question.release_id ?? 0) > (existing.release_id ?? 0) ||
            ((question.release_id ?? 0) === (existing.release_id ?? 0) && question.test_question_id > existing.test_question_id)
        ) {
            bestBySubject.set(question.subject_id, question);
        }
    }
    return [...bestBySubject.values()];
}

function groupTestQuestionsByGuid(questions: ZybookTestQuestion[]): Map<string, ZybookTestQuestion[]> {
    const groups = new Map<string, ZybookTestQuestion[]>();
    for (const question of questions) {
        const key = getTestQuestionGuidKey(question);
        const list = groups.get(key) ?? [];
        list.push(question);
        groups.set(key, list);
    }
    return groups;
}

function buildTestQuestionDocAttrs(questions: ZybookTestQuestion[]): Record<string, unknown> {
    const firstQuestion = questions[0];
    const docAttrs: Record<string, unknown> = {};

    if (firstQuestion?.release_id != null) {
        docAttrs.releaseId = firstQuestion.release_id;
    }

    return docAttrs;
}

function createDefaultParagraphAttrs() {
    return {
        id: uuidv4(),
        textAlign: null,
        nodeTextAlign: null,
        nodeVerticalAlign: null,
        backgroundColor: null,
    };
}

function createDefaultZyCodeAttrs(language = 'python') {
    return {
        id: uuidv4(),
        language,
        obeyleadingwhitespace: false,
        highlight: null,
        nobox: false,
        numbering: false,
    };
}

function createDefaultInlineMathAttrs(latex = '') {
    return {
        id: uuidv4(),
        latex,
        mathml: '',
    };
}

const DEFAULT_BLOCK_MATH_ATTRS = {
    latex: '',
    mathml: '',
};

function createDefaultImageAttrs(overrides: { src?: string; alt?: string; width?: string | null } = {}) {
    return {
        id: uuidv4(),
        src: overrides.src ?? '',
        alt: overrides.alt ?? '',
        title: null,
        width: overrides.width ?? null,
        height: null,
        renderAsHTML: false,
        pull: null,
        aspectRatio: null,
    };
}

function convertTableCellInlineToParagraph(nodes: ZybookTestQuestionNode[] | null | undefined): JSONContent {
    const content = convertInlineNodes(nodes);
    return {
        type: 'paragraph',
        attrs: createDefaultParagraphAttrs(),
        content: content.length > 0 ? content : undefined,
    };
}

function convertTableHeaderNode(node: ZybookTestQuestionNode): JSONContent {
    const classAttr = typeof node.data?.class === 'string' ? node.data.class : '';
    return {
        type: 'tableHeader',
        attrs: {
            primaryKey: classAttr === 'dbprimarykey',
            foreignKey: classAttr === 'dbforeignkey',
        },
        content: [convertTableCellInlineToParagraph(node.nodes)],
    };
}

function convertTableCellNode(node: ZybookTestQuestionNode): JSONContent {
    return {
        type: 'tableCell',
        content: [convertTableCellInlineToParagraph(node.nodes)],
    };
}

function convertTableRowNode(node: ZybookTestQuestionNode): JSONContent {
    return {
        type: 'tableRow',
        content: (node.nodes ?? []).map(cellNode => {
            if (cellNode.type === 'header') {
                return convertTableHeaderNode(cellNode);
            }
            return convertTableCellNode(cellNode);
        }),
    };
}

function convertTableNode(node: ZybookTestQuestionNode): JSONContent {
    return {
        type: 'table',
        attrs: {
            isDatabaseTable: node.data?.class === 'dbtable',
            isLayoutGrid: false,
        },
        content: (node.nodes ?? []).filter(row => row.type === 'row').map(convertTableRowNode),
    };
}

function isZybookTestQuestion(value: unknown): value is ZybookTestQuestion {
    if (!value || typeof value !== 'object') {
        return false;
    }
    const candidate = value as ZybookTestQuestion;
    return (
        typeof candidate.test_question_id === 'number' &&
        !!candidate.payload &&
        candidate.payload.type === 'question' &&
        Array.isArray(candidate.payload.nodes)
    );
}

function isZybookTestQuestionPayload(value: unknown): value is ZybookTestQuestion['payload'] {
    if (!value || typeof value !== 'object') {
        return false;
    }
    const candidate = value as ZybookTestQuestion['payload'];
    return candidate.type === 'question' && Array.isArray(candidate.nodes);
}

export function parseZybookTestQuestionInput(input: unknown): ZybookTestQuestion[] {
    let parsed = input;

    if (typeof input === 'string') {
        parsed = JSON.parse(input);
    }

    if (Array.isArray(parsed)) {
        const questions = parsed.filter(isZybookTestQuestion);
        if (questions.length === 0) {
            throw new Error('Expected an array of zybook test question objects.');
        }
        return questions;
    }

    if (isZybookTestQuestion(parsed)) {
        return [parsed];
    }

    if (isZybookTestQuestionPayload(parsed)) {
        return [
            {
                test_question_id: 0,
                payload: parsed,
            },
        ];
    }

    throw new Error('Invalid test question input. Expected a test question object or array.');
}

function stripHtml(html: string): string {
    return html
        .replace(/<[^>]+>/g, '')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&');
}

function resolveImageSrc(url: string | undefined, baseImageUrl?: string | null): string {
    if (!url) {
        return '';
    }
    if (baseImageUrl && url.includes('/IMAGES/')) {
        const fileName = url.split('/IMAGES/')[1]?.split('?')[0];
        if (fileName) {
            return `${baseImageUrl}${fileName}`;
        }
    }
    if (url.startsWith('zyAuthor/')) {
        return `https://zytools.zybooks.com/${url}`;
    }
    if (/^https?:\/\//i.test(url)) {
        return url;
    }
    if (url.includes('/IMAGES/')) {
        const fileName = url.split('/IMAGES/')[1]?.split('?')[0];
        if (fileName && baseImageUrl) {
            return `${baseImageUrl}${fileName}`;
        }
    }
    if (baseImageUrl && !url.startsWith(baseImageUrl)) {
        const fileName = url.split('/').pop();
        if (fileName) {
            return `${baseImageUrl}${fileName}`;
        }
    }
    return url;
}

function convertTextMarks(marks?: { type?: ZybookTestQuestionTextNodeMark }[]): ProsemirrorTextMark[] | undefined {
    if (!marks?.length) {
        return undefined;
    }

    const converted: ProsemirrorTextMark[] = [];
    for (const mark of marks) {
        switch (mark.type) {
            case 'code':
                converted.push({ type: 'code' });
                break;
            case 'italic':
                converted.push({ type: 'italic' });
                break;
            case 'bold':
                converted.push({ type: 'bold' });
                break;
        }
    }

    return converted.length > 0 ? converted : undefined;
}

function convertBlatexTextNode(node: ZybookTestQuestionNode): JSONContent[] {
    const text = node.text ?? '';
    const latex = text.replace(/^\\\[/, '').replace(/\\\]$/, '');
    return [
        {
            type: 'blockMath',
            attrs: {
                ...DEFAULT_BLOCK_MATH_ATTRS,
                latex,
            },
        },
    ];
}

function convertInlineTextNode(node: ZybookTestQuestionNode): JSONContent[] {
    const text = node.text ?? '';
    const hasIlatex = node.marks?.some(mark => mark.type === 'ilatex');

    if (hasIlatex) {
        const latex = text.replace(/^\\\(/, '').replace(/\\\)$/, '');
        return [
            {
                type: 'inlineMath',
                attrs: createDefaultInlineMathAttrs(latex),
            },
        ];
    }

    const marks = convertTextMarks(node.marks);
    const textNode: JSONContent = { type: 'text', text };
    if (marks?.length) {
        textNode.marks = marks;
    }
    return [textNode];
}

function convertInlineNodes(nodes: ZybookTestQuestionNode[] | null | undefined): JSONContent[] {
    if (!nodes?.length) {
        return [];
    }
    const content: JSONContent[] = [];
    nodes.forEach((node, index) => {
        if (node.kind === 'text') {
            content.push(...convertInlineTextNode(node));
            return;
        }
        if (node.type === 'line') {
            content.push(...convertInlineNodes(node.nodes));
            if (index < nodes.length - 1) {
                content.push({ type: 'hardBreak' });
            }
        }
    });
    return content;
}

function convertLineBlocksToParagraph(lineNodes: ZybookTestQuestionNode[]): JSONContent | null {
    if (lineNodes.length === 0) {
        return null;
    }

    const content: JSONContent[] = [];
    lineNodes.forEach((lineNode, index) => {
        if (index > 0) {
            content.push({ type: 'hardBreak' });
        }
        content.push(...convertInlineNodes(lineNode.nodes));
    });

    if (content.length === 0) {
        return null;
    }

    return {
        type: 'paragraph',
        attrs: createDefaultParagraphAttrs(),
        content,
    };
}

function convertBlockNode(node: ZybookTestQuestionNode, options?: ConvertZybookTestQuestionsOptions): JSONContent[] {
    switch (node.type) {
        case 'paragraph':
            return [
                {
                    type: 'paragraph',
                    attrs: createDefaultParagraphAttrs(),
                    content: convertInlineNodes(node.nodes),
                },
            ];
        case 'line': {
            const paragraph = convertLineBlocksToParagraph([node]);
            return paragraph ? [paragraph] : [];
        }
        case 'codeBlock': {
            const rawText = (typeof node.data?.raw_text === 'string' && node.data.raw_text) || stripHtml(node.nodes?.[0]?.text ?? '');
            const language = typeof node.data?.language === 'string' ? node.data.language : 'python';
            return [
                {
                    type: 'zyCode',
                    attrs: createDefaultZyCodeAttrs(language),
                    content: rawText ? [{ type: 'text', text: rawText }] : undefined,
                },
            ];
        }
        case 'image': {
            const url = typeof node.data?.url === 'string' ? node.data.url : '';
            const alt = typeof node.data?.alt === 'string' ? node.data.alt : '';
            const width = node.data?.width != null ? String(node.data.width) : null;
            return [
                {
                    type: 'image',
                    attrs: createDefaultImageAttrs({
                        src: resolveImageSrc(url, options?.baseImageUrl),
                        alt,
                        width,
                    }),
                },
            ];
        }
        case 'table':
            return [convertTableNode(node)];
        default:
            return [];
    }
}

function flushTextNodeGroup(textNodes: ZybookTestQuestionNode[]): JSONContent | null {
    const content = textNodes.flatMap(convertInlineTextNode);
    if (content.length === 0) {
        return null;
    }
    return {
        type: 'paragraph',
        attrs: createDefaultParagraphAttrs(),
        content,
    };
}

function convertBlockNodes(nodes: ZybookTestQuestionNode[] | null | undefined, options?: ConvertZybookTestQuestionsOptions): JSONContent[] {
    if (!nodes?.length) {
        return [{ type: 'paragraph', attrs: createDefaultParagraphAttrs() }];
    }

    const converted: JSONContent[] = [];
    let textGroup: ZybookTestQuestionNode[] = [];
    let lineGroup: ZybookTestQuestionNode[] = [];

    const flushTextGroup = () => {
        const paragraph = flushTextNodeGroup(textGroup);
        if (paragraph) {
            converted.push(paragraph);
        }
        textGroup = [];
    };

    const flushLineGroup = () => {
        const paragraph = convertLineBlocksToParagraph(lineGroup);
        if (paragraph) {
            converted.push(paragraph);
        }
        lineGroup = [];
    };

    for (const node of nodes) {
        if (node.kind === 'text') {
            if (node.marks?.some(mark => mark.type === 'blatex')) {
                flushTextGroup();
                flushLineGroup();
                converted.push(...convertBlatexTextNode(node));
                continue;
            }
            flushLineGroup();
            textGroup.push(node);
            continue;
        }

        if (node.type === 'line') {
            flushTextGroup();
            lineGroup.push(node);
            continue;
        }

        flushTextGroup();
        flushLineGroup();
        converted.push(...convertBlockNode(node, options));
    }

    flushTextGroup();
    flushLineGroup();

    return converted.length > 0 ? converted : [{ type: 'paragraph', attrs: createDefaultParagraphAttrs() }];
}

export function convertZybookTestQuestionToProsemirror(
    question: ZybookTestQuestion,
    options?: ConvertZybookTestQuestionsOptions,
): JSONContent {
    const payloadNodes = question.payload?.nodes ?? [];
    const instructionsNode = payloadNodes.find(node => node.type === 'instructions');
    const choicesNode = payloadNodes.find(node => node.type === 'choices');

    const choiceNodes: JSONContent[] =
        choicesNode?.nodes?.map(choice => ({
            type: 'testQuestionChoice',
            attrs: {
                id: uuidv4(),
                isCorrect: choice.data?.correct === 'true',
            },
            content: convertBlockNodes(choice.nodes, options),
        })) ?? [];

    return {
        type: 'testQuestion',
        attrs: {
            id: question.guid ?? uuidv4(),
            testQuestionId: question.test_question_id,
            gdocId: question.gdoc_id ?? null,
            showInEval: question.show_in_eval ?? false,
        },
        content: [
            {
                type: 'testQuestionInstructions',
                attrs: { id: uuidv4() },
                content: convertBlockNodes(instructionsNode?.nodes, options),
            },
            ...choiceNodes,
        ],
    };
}

export function convertZybookTestQuestionsToProsemirror(
    questions: ZybookTestQuestion[],
    options?: ConvertZybookTestQuestionsOptions,
): JSONContent {
    const docAttrs = buildTestQuestionDocAttrs(questions);
    return {
        type: 'doc',
        attrs: Object.keys(docAttrs).length > 0 ? docAttrs : {},
        content: questions.map(question => convertZybookTestQuestionToProsemirror(question, options)),
    };
}

export function convertZybookTestQuestionsForImport(
    questions: ZybookTestQuestion[],
    options: ConvertZybookTestQuestionsForImportOptions,
): JSONContent {
    const { sameContentSourceSubjectCount } = options;
    const docAttrs = buildTestQuestionDocAttrs(questions);
    const guidGroups = groupTestQuestionsByGuid(questions);
    const sortedGuidEntries = [...guidGroups.entries()].sort(([, variantsA], [, variantsB]) => {
        const minA = Math.min(...variantsA.map(q => q.number ?? Number.MAX_SAFE_INTEGER));
        const minB = Math.min(...variantsB.map(q => q.number ?? Number.MAX_SAFE_INTEGER));
        if (minA !== minB) {
            return minA - minB;
        }
        return getTestQuestionGuidKey(variantsA[0]!).localeCompare(getTestQuestionGuidKey(variantsB[0]!));
    });

    const content: JSONContent[] = [];
    for (const [guid, variants] of sortedGuidEntries) {
        const dedupedVariants = dedupeTestQuestionVariantsBySubject(variants);
        if (isUniversalTestQuestionGuid(dedupedVariants, guid, sameContentSourceSubjectCount)) {
            content.push(convertZybookTestQuestionToProsemirror(pickRepresentativeTestQuestion(dedupedVariants), options));
            continue;
        }

        const sortedVariants = [...dedupedVariants].sort((a, b) =>
            normalizeVersion(a.version ?? '').localeCompare(normalizeVersion(b.version ?? '')),
        );
        for (const variant of sortedVariants) {
            content.push({
                type: 'zyVersionBlock',
                attrs: {
                    id: uuidv4(),
                    version: normalizeVersion(variant.version ?? ''),
                },
                content: [convertZybookTestQuestionToProsemirror(variant, options)],
            });
        }
    }

    return {
        type: 'doc',
        attrs: Object.keys(docAttrs).length > 0 ? docAttrs : undefined,
        content,
    };
}
